// BackButton — shared back-nav element used across in-app and public pages.
// - `to` (string)  → explicit navigation target. If omitted, falls back to
//   `navigate(-1)` (browser back) when there is history, otherwise the `fallback`.
// - `fallback`     → path used when there is no browser history (e.g. when a
//   page is loaded directly via deep-link). Defaults to "/app".
// - `label`        → button text. Defaults to "Back".
// - `variant`      → 'icon' (compact arrow only, used in tight headers) or
//   'full' (arrow + label, used as a row above page titles). Defaults to 'full'.

import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export default function BackButton({
  to,
  fallback = '/app',
  label = 'Back',
  variant = 'full',
  className,
  testId = 'back-button',
}) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (to) {
      navigate(to);
      return;
    }
    // window.history.length includes the initial blank entry, so > 1 means
    // there is a real prior page we can go back to.
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={cn(
          'inline-flex items-center justify-center w-9 h-9 rounded-md text-[#5D524D] hover:text-[#2C2420] hover:bg-[#F5F3F0] transition-colors',
          className,
        )}
        data-testid={testId}
        title={label}
        aria-label={label}
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={cn(
        'gap-1.5 text-[#5D524D] hover:text-[#2C2420] hover:bg-[#F5F3F0] -ml-2',
        className,
      )}
      data-testid={testId}
    >
      <ArrowLeft className="w-4 h-4" />
      {label}
    </Button>
  );
}
